
import React, { useState, useEffect, useCallback } from "react";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Table,
  Button,
  InputGroup,
  InputGroupText,
  Input,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import { get, post, uri } from "helpers/api_helper";
import * as api from "../../constants/api_constants";
import secureLocalStorage from "react-secure-storage";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "helpers/toastMessages";
import SummaryCards from "components/SummaryCards";
import CustomPagination from "components/CustomPagination";
import { CustomButton } from "components/CommonInputs/CustomButtons/CustomButton";
import { formatToPascal } from "helpers/utils/nameUtils";
import axios from "axios";

// Custom debounce hook
const useDebounce = (value, delay) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
      console.log("Debounced search query:", value);
    }, delay);
    return () => clearTimeout(handler);
  }, [value, delay]);
  return debouncedValue;
};

const ManagerTimesheets = () => {
  const navigate = useNavigate();
  const [timesheetCount, setTimesheetCount] = useState({
    managerCounts: {},
    employeeCounts: {},
  });
  const [employeeTimesheets, setEmployeeTimesheets] = useState([]);
  console.log("employeeTimesheets", employeeTimesheets);

  const [managerTimesheets, setManagerTimesheets] = useState([]);
  console.log("managerTimesheets", managerTimesheets);

  const [searchInput, setSearchInput] = useState("");
  const searchQuery = useDebounce(searchInput, 300);
  const [activeTab, setActiveTab] = useState("ALL");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [selectedTimesheet, setSelectedTimesheet] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  console.log("totalPages", totalPages);

  const [projectsMap, setProjectsMap] = useState(new Map());
  const [projectsFetched, setProjectsFetched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCountLoading, setIsCountLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timesheetFiles, setTimesheetFiles] = useState([]);
  console.log("timesheetFiles", timesheetFiles);
  const employeeId = secureLocalStorage.getItem("employeeId");
  console.log("employeeId", employeeId);

  useEffect(() => {
    console.log("Resetting page due to tab or search change:", {
      activeTab,
      searchQuery,
    });
    setCurrentPage(1);
    setTotalPages(0);
  }, [activeTab, searchQuery]);

  const fetchAllProjects = useCallback(
    async (employeeId) => {
      if (projectsFetched) {
        console.log("Projects already fetched, skipping fetchAllProjects");
        return;
      }
      setError(null);
      console.log("Fetching projects from:", api.GET_ALL_PROJECTS_ID_NAME);
      try {
        const response = await get(api.GET_ALL_PROJECTS_ID_NAME(employeeId));
        console.log("Projects response:", response);

        const projectsData =
          response?.paginationResponse?.data || response?.data || [];
        if (!Array.isArray(projectsData)) {
          throw new Error("Invalid projects data format");
        }
        const projectMap = new Map(
          projectsData
            .filter((proj) => proj.projectId && proj.projectName)
            .map((proj) => [proj.projectId, formatToPascal(proj.projectName)])
        );
        setProjectsMap(projectMap);
        setProjectsFetched(true);
        console.log(
          "Processed projects map:",
          JSON.stringify([...projectMap], null, 2)
        );
      } catch (err) {
        console.error("Error fetching projects:", {
          message: err.message,
          stack: err.stack,
          response: err.response?.data,
          status: err.response?.status,
        });
        setError("Failed to fetch projects. Check console for details.");
        showErrorToast("Failed to fetch projects.");
        setProjectsMap(new Map());
      }
    },
    [projectsFetched]
  );

  const fetchCount = useCallback(
    async (retries = 3, delay = 1000) => {
      if (!employeeId) {
        console.error("No employeeId found in secureLocalStorage");
        setError("Employee ID is missing. Please log in again.");
        showErrorToast("Employee ID is missing.");
        return;
      }

      setIsCountLoading(true);
      setError(null);

      for (let attempt = 1; attempt <= retries; attempt++) {
        try {
          const endpoint = api.GET_TIMESHEET_COUNT(employeeId);
          console.log(`Fetching count (attempt ${attempt}) from:`, endpoint);
          const response = await get(endpoint);
          console.log("Full API response:", JSON.stringify(response, null, 2));

          if (
            response?.status === "true" &&
            response?.code === 200 &&
            Array.isArray(response?.data) &&
            response.data[0]
          ) {
            const { managerCounts, employeeCounts } = response.data[0];
            setTimesheetCount({
              managerCounts: managerCounts || {
                totalHours: 0,
                totalSubmitted: 0,
                totalApproved: 0,
                totalRejected: 0,
                totalDraft: 0,
              },
              employeeCounts: employeeCounts || {
                totalHours: 0,
                totalSubmitted: 0,
                totalApproved: 0,
                totalRejected: 0,
                totalDraft: 0,
              },
            });
            console.log("Updated timesheetCount:", {
              managerCounts,
              employeeCounts,
            });
            setIsCountLoading(false);
            return;
          } else {
            console.warn("Unexpected response format:", response);
            showWarningToast("No timesheet counts available.");
            setTimesheetCount({
              managerCounts: {},
              employeeCounts: {},
            });
          }
        } catch (error) {
          const status = error.response?.status;
          let errorMessage = "Failed to fetch timesheet counts.";
          if (status === 404) {
            errorMessage = "Timesheet count endpoint not found.";
          } else if (status === 401) {
            errorMessage = "Unauthorized. Please check your authentication.";
          } else if (status === 500) {
            errorMessage = "Server error. Please try again later.";
          }

          console.error(
            `Error fetching timesheet count (attempt ${attempt}):`,
            {
              message: error.message,
              stack: error.stack,
              response: error.response?.data,
              status,
            }
          );

          if (attempt === retries) {
            setError(`${errorMessage} Check console for details.`);
            showErrorToast(errorMessage);
            setTimesheetCount({
              managerCounts: {},
              employeeCounts: {},
            });
            setIsCountLoading(false);
            return;
          }

          await new Promise((resolve) => setTimeout(resolve, delay));
        }
      }
      setIsCountLoading(false);
    },
    [employeeId]
  );

  const fetchTimesheets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const payload = {
        searchKey: searchQuery.trim() || "",
        page: currentPage - 1,
        pageSize: 10,
        sortBy: "timesheetCode",
        direction: "ASC",
        timesheetDTO: {
          subscriptionId: 100,
          employeeId: employeeId,
          status:
            activeTab === "EMPLOYEE" || activeTab === "ALL" ? null : activeTab,
        },
      };

      const response = await post(api.GET_ALL_TIMESHEETS_BY_SEARCH, payload);
      console.log("Timesheets API response:", response);

      const empData = response?.data?.employeeTimesheets?.data || [];
      const managerData = response?.data?.managerTimesheets?.data || [];

      // Fetch file details for timesheets with clientDocuments
      const timesheetDoc = [
        ...empData.filter((item) => item.clientDocuments),
        ...managerData.filter((item) => item.clientDocuments),
      ].map((item) => item.clientDocuments);

      const fileDetails = await Promise.all(
        timesheetDoc.map(async (fileId) => {
          try {
            const fileResponse = await get(
              `${uri}/fileservice/api/files/view/${fileId}`
            );
            return {
              id: fileResponse.id,
              fileName: fileResponse.fileName,
              name: fileResponse.fileName,
              path: fileResponse.path || `/Uploads/${fileResponse.fileName}`,
            };
          } catch (error) {
            console.error(`Error fetching file ${fileId}:`, error);
            return null;
          }
        })
      );
      setTimesheetFiles(fileDetails.filter((file) => file !== null));

      if (!empData.length) {
        console.log(
          "No employee timesheets returned for search query:",
          searchQuery
        );
      }

      if (!managerData.length) {
        console.log(
          "No manager timesheets returned for search query:",
          searchQuery
        );
      }

      const processedEmpData = empData.map((ts) => {
        const projectId =
          ts.projectId && !isNaN(ts.projectId) && ts.projectId > 0
            ? ts.projectId
            : null;
        const projectName =
          projectId && projectsMap.has(projectId)
            ? projectsMap.get(projectId)
            : projectId
            ? formatToPascal(`Unknown Project ${projectId}`)
            : "N/A";

        return {
          ...ts,
          employeeName: formatToPascal(ts.employeeName || "N/A"),
          projectId: projectId || null,
          projectName,
          assignmentTitle: formatToPascal(ts.assignmentTitle || "N/A"),
        };
      });

      const processedManagerData = managerData.map((ts) => {
        const projectId =
          ts.projectId && !isNaN(ts.projectId) && ts.projectId > 0
            ? ts.projectId
            : null;
        const projectName =
          projectId && projectsMap.has(projectId)
            ? projectsMap.get(projectId)
            : projectId
            ? formatToPascal(`Unknown Project ${projectId}`)
            : "N/A";

        return {
          ...ts,
          employeeName: formatToPascal(ts.employeeName || "N/A"),
          projectId: projectId || null,
          projectName,
          assignmentTitle: formatToPascal(ts.assignmentTitle || "N/A"),
        };
      });

      setEmployeeTimesheets(processedEmpData);
      setManagerTimesheets(processedManagerData);

      // Set total pages based on active tab
      const totalPages =
        activeTab === "EMPLOYEE"
          ? response?.data?.managerTimesheets?.totalPages || 0
          : response?.data?.employeeTimesheets?.totalPages || 0;
      setTotalPages(totalPages);

      if (!empData.length && !managerData.length) {
        showWarningToast(
          "No timesheets found for the selected filters or search query."
        );
      }
    } catch (error) {
      console.error("Error fetching timesheets:", {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError("Failed to fetch timesheets. Check console for details.");
      showErrorToast("Failed to fetch timesheets.");
      setEmployeeTimesheets([]);
      setManagerTimesheets([]);
      setTotalPages(0);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, searchQuery, currentPage, employeeId, projectsMap]);

  const handleDownloadFile = async (timesheet) => {
    const fileId = timesheet.clientDocuments;
    console.log("Attempting to download file with ID:", fileId);

    if (!fileId) {
      showWarningToast("No file associated with this timesheet.");
      return;
    }

    const file = timesheetFiles.find((f) => f.id == fileId);
    console.log("File Details:", file);
    if (!file) {
      showErrorToast("File details not found.");
      return;
    }

    const fileExtension = file.fileName.split(".").pop().toLowerCase();
    const mimeTypes = {
      pdf: "application/pdf",
      docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      doc: "application/msword",
    };
    const mimeType = mimeTypes[fileExtension] || "application/octet-stream";

    try {
      const response = await axios.get(
        `${uri}/fileservice/api/files/download/${fileId}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response?.data], { type: mimeType });
      console.log("Blob Size:", blob.size);

      if (blob.size === 0) {
        showErrorToast("Downloaded file is empty.");
        return;
      }

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", file.fileName);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", {
        message: err.message,
        stack: err.stack,
        response: err.response,
        status: err.response?.status,
      });
      let errorMessage = "Failed to download file.";
      if (err.response?.status === 404) {
        errorMessage = "File not found on the server.";
      } else if (err.response?.status === 401) {
        errorMessage = "Unauthorized access. Please check your authentication.";
      } else if (err.response?.status === 500) {
        errorMessage = "Server error. Please try again later.";
      }
      showErrorToast(errorMessage);
    }
  };

  const handleApprove = useCallback(
    async (timesheetId) => {
      setError(null);
      try {
        const payload = {
          timesheetDTO: {
            subscriptionId: 100,
            employeeId: employeeId,
            status: "APPROVED",
          },
        };
        console.log(
          "Approving timesheet:",
          timesheetId,
          JSON.stringify(payload, null, 2)
        );
        const response = await post(
          api.TIMESHEET_APPROVER(timesheetId),
          payload
        );
        console.log("Approve response:", JSON.stringify(response, null, 2));

        if (response.status) {
          showSuccessToast("Timesheet Approved Successfully");
          await fetchTimesheets();
        } else {
          throw new Error("Failed to approve timesheet");
        }
      } catch (error) {
        console.error("Error approving timesheet:", {
          message: error.message,
          timesheetId,
          response: error.response?.data,
          status: error.response?.status,
        });
        setError("Failed to approve timesheet. Check console for details.");
        showErrorToast("Error approving timesheet");
      }
    },
    [employeeId, fetchTimesheets]
  );

  const handleReject = useCallback((timesheetId) => {
    console.log("Opening reject modal for timesheet:", timesheetId);
    setSelectedTimesheet(timesheetId);
    setShowRejectModal(true);
  }, []);

  const confirmReject = useCallback(async () => {
    setError(null);
    try {
      if (!selectedTimesheet || !rejectionReason) {
        showWarningToast("Please provide a rejection reason.");
        return;
      }

      const payload = {
        timesheetDTO: {
          subscriptionId: 100,
          employeeId: employeeId,
          status: "REJECTED",
          rejectionReason: rejectionReason,
        },
      };
      console.log(
        "Rejecting timesheet:",
        selectedTimesheet,
        JSON.stringify(payload, null, 2)
      );
      const response = await post(
        api.TIMESHEET_APPROVER(selectedTimesheet),
        payload
      );
      console.log("Reject response:", JSON.stringify(response, null, 2));

      if (response.status) {
        showWarningToast("Timesheet is rejected");
        setShowRejectModal(false);
        setRejectionReason("");
        setSelectedTimesheet(null);
        await fetchTimesheets();
      } else {
        throw new Error("Failed to reject timesheet");
      }
    } catch (error) {
      console.error("Error rejecting timesheet:", {
        message: error.message,
        timesheetId: selectedTimesheet,
        response: error.response?.data,
        status: error.response?.status,
      });
      setError("Failed to reject timesheet. Check console for details.");
      showErrorToast("Error rejecting timesheet");
    }
  }, [selectedTimesheet, rejectionReason, employeeId, fetchTimesheets]);

  useEffect(() => {
    console.log("Triggering fetchAllProjects");
    fetchAllProjects(employeeId);
  }, [fetchAllProjects]);

  useEffect(() => {
    console.log("Triggering fetchTimesheets and fetchCount");
    fetchTimesheets();
    fetchCount();
  }, [fetchTimesheets, fetchCount]);

  const getDisplayTimesheets = () => {
    if (activeTab === "EMPLOYEE") {
      return managerTimesheets || [];
    }
    if (activeTab === "ALL") {
      return employeeTimesheets || [];
    }
    return (employeeTimesheets || []).filter(
      (timesheet) => timesheet?.status === activeTab
    );
  };

  const handlePageChange = (page) => {
    console.log("Changing page to:", page);
    setCurrentPage(page);
  };

  const formatDateRange = (startDate, endDate) => {
    if (!startDate || !endDate) return "N/A";
    try {
      const start = new Date(startDate);
      const end = new Date(endDate);
      const weekNumber = getWeekNumber(start);
      return `Week ${weekNumber} / ${start.toLocaleDateString("en-US", {
        day: "2-digit",
        month: "short",
      })}-${end.toLocaleDateString("en-US", { day: "2-digit" })}`;
    } catch {
      return "Invalid Date";
    }
  };

  const getWeekNumber = (date) => {
    try {
      const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
      const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
      return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
    } catch {
      return 0;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toUpperCase()) {
      case "APPROVED":
        return "text-success";
      case "SUBMITTED":
        return "text-warning";
      case "REJECTED":
        return "text-danger";
      case "DRAFT":
        return "text-muted";
      default:
        return "text-muted";
    }
  };

  const timesheetCountData =
    activeTab === "EMPLOYEE"
      ? timesheetCount.managerCounts
      : timesheetCount.employeeCounts;

  const summaryData = [
    {
      iconClass: "fas fa-clock text-primary",
      label: "Total Hours",
      value: timesheetCountData?.totalHours || 0,
    },
    {
      iconClass: "fas fa-paper-plane text-warning",
      label: "Submitted Hours",
      value: timesheetCountData?.totalSubmitted || 0,
    },
    {
      iconClass: "fas fa-check-circle text-primary",
      label: "Approved Hours",
      value: timesheetCountData?.totalApproved || 0,
    },
    {
      iconClass: "fas fa-times-circle text-danger",
      label: "Rejected Hours",
      value: timesheetCountData?.totalRejected || 0,
    },
    {
      iconClass: "fas fa-save text-primary",
      label: "Draft Hours",
      value: timesheetCountData?.totalDraft || 0,
    },
  ];

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading="Timesheets Management"
          routePath="Home"
          pageName="Manager Timesheets"
        />
        <Row className="px-4">
          {error && (
            <div
              className="alert alert-danger alert-dismissible fade show"
              role="alert"
            >
              {error}
              <button
                type="button"
                className="btn-close"
                onClick={() => setError(null)}
                aria-label="Close"
              ></button>
            </div>
          )}
          {isCountLoading ? (
            <div className="text-center py-3">Loading counts...</div>
          ) : (
            <SummaryCards data={summaryData} />
          )}
          <Row className="py-1 px-4">
            <Col>
              <Row className="d-flex justify-content-between align-items-center py-2">
                <Col xs={3}>
                  <InputGroup size="sm" className="elbert-search-group">
                    <Input
                      placeholder="Search Timesheets"
                      className="elbert-search form-control"
                      value={searchInput}
                      onChange={(e) => {
                        console.log("Search input:", e.target.value);
                        setSearchInput(e.target.value);
                      }}
                      aria-label="Search timesheets"
                    />
                    <InputGroupText>
                      {isLoading ? (
                        <i className="fas fa-spinner fa-spin" />
                      ) : (
                        <i className="fa fa-search" />
                      )}
                    </InputGroupText>
                  </InputGroup>
                </Col>
                <Col
                  xs={3}
                  className="d-flex align-items-center justify-content-end"
                >
                  <CustomButton
                    text="+ Add New"
                    onClick={() => {
                      console.log("Navigating to add-timesheet");
                      navigate("/add-timesheet");
                    }}
                    aria-label="Add new timesheet"
                  />
                </Col>
              </Row>
              <Row>
                <Col>
                  <Nav tabs className="mb-3">
                    <NavItem>
                      <NavLink
                        className={activeTab === "ALL" ? "active" : ""}
                        onClick={() => setActiveTab("ALL")}
                        style={{ cursor: "pointer" }}
                      >
                        All Employee Timesheets
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "DRAFT" ? "active" : ""}
                        onClick={() => setActiveTab("DRAFT")}
                        style={{ cursor: "pointer" }}
                      >
                        Draft
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "SUBMITTED" ? "active" : ""}
                        onClick={() => setActiveTab("SUBMITTED")}
                        style={{ cursor: "pointer" }}
                      >
                        Pending Approval
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "APPROVED" ? "active" : ""}
                        onClick={() => setActiveTab("APPROVED")}
                        style={{ cursor: "pointer" }}
                      >
                        Approved
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "REJECTED" ? "active" : ""}
                        onClick={() => setActiveTab("REJECTED")}
                        style={{ cursor: "pointer" }}
                      >
                        Rejected
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={activeTab === "EMPLOYEE" ? "active" : ""}
                        onClick={() => setActiveTab("EMPLOYEE")}
                        style={{ cursor: "pointer" }}
                      >
                        My Timesheets
                      </NavLink>
                    </NavItem>
                  </Nav>
                  <Card>
                    <CardBody>
                      <div className="table-responsive">
                        <Table className="table mb-0">
                          <thead>
                            <tr>
                              <th style={{ width: "12%" }}>Timesheet Code</th>
                              <th style={{ width: "13%" }}>Week #/ Period</th>
                              <th style={{ width: "16%" }}>Employee Name</th>
                              <th style={{ width: "13%" }}>Project</th>
                              <th style={{ width: "16%" }}>Assignment</th>
                              <th style={{ width: "8%" }}>Total Hours</th>
                              <th style={{ width: "10%" }}>Status</th>
                              {activeTab === "SUBMITTED" && (
                                <th style={{ width: "8%" }}>Submission Date</th>
                              )}
                              <th
                                style={{
                                  width:
                                    activeTab === "SUBMITTED" ? "4%" : "12%",
                                }}
                              >
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {getDisplayTimesheets().map((ts) => (
                              <tr
                                key={ts?.timesheetId || ts?.id || Math.random()}
                              >
                                <td style={{ width: "12%" }}>
                                  {ts?.timesheetCode || "N/A"}
                                </td>
                                <td style={{ width: "13%" }}>
                                  {formatDateRange(
                                    ts?.periodStart,
                                    ts?.periodEnd
                                  )}
                                </td>
                                <td style={{ width: "16%" }}>
                                  {formatToPascal(ts?.employeeName) || "N/A"}
                                </td>
                                <td style={{ width: "13%" }}>
                                  {formatToPascal(ts?.projectName) ||
                                    (ts?.projectId
                                      ? `Project ID ${ts.projectId}`
                                      : "N/A")}
                                </td>
                                <td style={{ width: "16%" }}>
                                  {formatToPascal(ts?.assignmentTitle) || "N/A"}
                                </td>
                                <td style={{ width: "8%" }}>
                                  {ts?.totalHours || 0}
                                </td>
                                <td
                                  style={{ width: "10%" }}
                                  className={getStatusColor(ts?.status)}
                                >
                                  {ts?.status
                                    ? ts.status.charAt(0).toUpperCase() +
                                      ts.status.slice(1).toLowerCase()
                                    : "N/A"}
                                </td>
                                {activeTab === "SUBMITTED" && (
                                  <td style={{ width: "8%" }}>
                                    {ts?.submissionDate
                                      ? new Date(
                                          ts.submissionDate
                                        ).toLocaleDateString("en-US")
                                      : "-"}
                                  </td>
                                )}
                                <td
                                  style={{
                                    width:
                                      activeTab === "SUBMITTED" ? "4%" : "12%",
                                  }}
                                >
                                  <div className="d-flex gap-3 align-items-center">
                                    <i
                                      onClick={() =>
                                        navigate(
                                          `/view-timesheet/${ts?.timesheetId}`
                                        )
                                      }
                                      className="fa-solid fa-eye action-icon"
                                      title="View"
                                      aria-label={`View timesheet ${
                                        ts?.timesheetCode || "unknown"
                                      }`}
                                    />
                                    {ts.clientDocuments && (
                                      <i
                                        onClick={() => handleDownloadFile(ts)}
                                        className="fa-solid fa-download action-icon"
                                        title="Download"
                                        aria-label={`Download file for timesheet ${
                                          ts.timesheetCode || "unknown"
                                        }`}
                                      />
                                    )}
                                    {(ts?.status === "SUBMITTED" ||
                                      ts?.status === "DRAFT" ||
                                      ts?.status === "REJECTED") && (
                                      <i
                                        onClick={() =>
                                          navigate(
                                            `/add-timesheet/${ts?.timesheetId}`
                                          )
                                        }
                                        className="fa-regular fa-pen-to-square action-icon"
                                        title="Edit"
                                        aria-label={`Edit timesheet ${
                                          ts?.timesheetCode || "unknown"
                                        }`}
                                      />
                                    )}
                                    {activeTab !== "EMPLOYEE" &&
                                      ts?.status === "SUBMITTED" && (
                                        <>
                                          <i
                                            onClick={() =>
                                              handleApprove(ts?.timesheetId)
                                            }
                                            className="fas fa-check action-icon text-success"
                                            title={`Approve timesheet ${
                                              ts?.timesheetCode || "unknown"
                                            }`}
                                            aria-label={`Approve timesheet ${
                                              ts?.timesheetCode || "unknown"
                                            }`}
                                          />
                                          <i
                                            onClick={() =>
                                              handleReject(ts?.timesheetId)
                                            }
                                            className="fas fa-times action-icon text-danger"
                                            title={`Reject timesheet ${
                                              ts?.timesheetCode || "unknown"
                                            }`}
                                            aria-label={`Reject timesheet ${
                                              ts?.timesheetCode || "unknown"
                                            }`}
                                          />
                                        </>
                                      )}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                        {isLoading ? (
                          <div className="text-center py-3">Loading...</div>
                        ) : getDisplayTimesheets().length === 0 ? (
                          <div className="text-center py-3">
                            No timesheets found for the selected filters or
                            search query.
                          </div>
                        ) : null}
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            </Col>
          </Row>
          <CustomPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            aria-label="Timesheet pagination"
          />
          <Modal
            isOpen={showRejectModal}
            toggle={() => setShowRejectModal(false)}
          >
            <ModalHeader toggle={() => setShowRejectModal(false)}>
              Reject Timesheet Entry
            </ModalHeader>
            <ModalBody>
              <Form>
                <FormGroup>
                  <Label for="rejectionReason">
                    Please provide a reason for rejecting this timesheet entry:
                  </Label>
                  <Input
                    type="textarea"
                    id="rejectionReason"
                    rows={4}
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter rejection reason..."
                  />
                </FormGroup>
              </Form>
            </ModalBody>
            <ModalFooter>
              <Button
                color="secondary"
                onClick={() => setShowRejectModal(false)}
              >
                Cancel
              </Button>
              <Button color="danger" onClick={confirmReject}>
                Confirm Reject
              </Button>
            </ModalFooter>
          </Modal>
        </Row>
      </Container>
    </div>
  );
};

export default ManagerTimesheets;
